import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import DataTable from '../components/DataTable';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import {
  parteEntradaService, sucursalService, almacenService,
  tiposMovimientoService, trabajadorService, tablaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/requerimientos.css';
import '../styles/ordenes_compra.css';
import '../styles/datatable.css';

const today = () => new Date().toISOString().split('T')[0];
const fmtN = (v, d = 2) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const grRef = (serie, numero) => `GR${serie ? '-' + serie.trim() : ''}-${numero}`;

const ESTADO_GR = {
  GENERADO:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
  TRASLADADO:{ color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)'  },
  ANULADO:   { color: '#64748b', bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.2)' },
};

const PeDesdeGr = () => {
  const { sucursal: sesionSucursal } = useAuth();
  const [view, setView] = useState('list');   // 'list' | 'form' | 'creado'
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creado, setCreado] = useState(null);

  const [grsLista, setGrsLista] = useState([]);
  const [grSeleccionada, setGrSeleccionada] = useState(null);
  const [grItems, setGrItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [seleccionados, setSeleccionados] = useState({});

  const [almacenes, setAlmacenes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [tiposMov, setTiposMov] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);

  const [nextNumero, setNextNumero] = useState(null);
  const [form, setForm] = useState({
    id_sucursal: '', id_almacen: '', id_tipo_movimiento: '',
    fecha_operacion: today(), observacion: '',
    id_centro_costo: '', id_solicitante: '',
  });

  /* ── Cargar lista ─────────────────────────────── */
  const fetchGrs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await parteEntradaService.getGrsPendientes();
      setGrsLista(res.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar guías de remisión.') });
    } finally { setLoading(false); }
  }, []);

  const fetchCatalogos = useCallback(async () => {
    try {
      const [suc, alm, tm, trab, cc] = await Promise.all([
        sucursalService.getAll(), almacenService.getAll(),
        tiposMovimientoService.getAll(), trabajadorService.getAll(),
        tablaService('centros-costo').getAll(),
      ]);
      setSucursales(suc.data || []);
      setAlmacenes(alm.data || []);
      setTiposMov((tm.data || []).filter(t => t.tipo === 'ENTRADA'));
      setTrabajadores(trab.data || []);
      setCentrosCosto(cc.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchGrs(); fetchCatalogos(); }, [fetchGrs, fetchCatalogos]);

  useEffect(() => {
    if (!form.id_almacen) { setNextNumero(null); return; }
    parteEntradaService.getNextNumero(form.id_almacen)
      .then(r => setNextNumero(r.data.numero))
      .catch(() => setNextNumero(null));
  }, [form.id_almacen]);

  /* ── Seleccionar GR ───────────────────────────── */
  const seleccionarGr = async (gr) => {
    setGrSeleccionada(gr);
    setSeleccionados({});
    setGrItems([]);
    const ti = tiposMov.find(t => t.codigo === 'TI') || tiposMov[0];   // TI = Traslado Ingreso
    // Preselecciona almacén destino si el usuario está en esa sucursal
    const almDestino = almacenes.find(a => a.id_almacen === gr.id_almacen_destino);
    setForm({
      id_sucursal: almDestino?.id_sucursal ? String(almDestino.id_sucursal) : (sesionSucursal?.id_sucursal ? String(sesionSucursal.id_sucursal) : ''),
      id_almacen: almDestino ? String(almDestino.id_almacen) : '',
      id_tipo_movimiento: ti ? String(ti.id_tipo_movimiento) : '',
      fecha_operacion: today(),
      observacion: `Recepción GR ${grRef(gr.serie_codigo, gr.numero)}`,
      id_centro_costo: '', id_solicitante: '',
    });
    setCreado(null);
    setView('form');
    setLoadingItems(true);
    try {
      const res = await parteEntradaService.getGrItems(gr.id_guia);
      setGrItems(res.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar ítems de la GR.') });
    } finally { setLoadingItems(false); }
  };

  /* ── Selección ítems ──────────────────────────── */
  const toggleItem = (it) => setSeleccionados(prev => {
    if (it.id_detalle in prev) { const n = { ...prev }; delete n[it.id_detalle]; return n; }
    const pend = parseFloat(it.cantidad_pendiente) || 0;
    return { ...prev, [it.id_detalle]: pend > 0 ? pend : parseFloat(it.cantidad) || 1 };
  });
  const setCantidad = (id, val) => setSeleccionados(prev => ({ ...prev, [id]: val }));
  const toggleAll = () => {
    const aptos = grItems.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0);
    const todos = aptos.length > 0 && aptos.every(it => it.id_detalle in seleccionados);
    if (todos) setSeleccionados(prev => { const n = { ...prev }; aptos.forEach(it => delete n[it.id_detalle]); return n; });
    else { const add = {}; aptos.forEach(it => { if (!(it.id_detalle in seleccionados)) add[it.id_detalle] = parseFloat(it.cantidad_pendiente) || 1; }); setSeleccionados(prev => ({ ...prev, ...add })); }
  };

  /* ── Guardar ──────────────────────────────────── */
  const handleSubmit = async () => {
    if (!form.id_almacen)         { setAlert({ type: 'warning', message: 'Seleccione un almacén.' }); return; }
    if (!form.id_tipo_movimiento) { setAlert({ type: 'warning', message: 'Seleccione un tipo de movimiento.' }); return; }
    if (!form.fecha_operacion)    { setAlert({ type: 'warning', message: 'Ingrese la fecha de operación.' }); return; }
    const sel = grItems.filter(it => it.id_detalle in seleccionados);
    if (sel.length === 0) { setAlert({ type: 'warning', message: 'Seleccione al menos un artículo.' }); return; }
    for (const it of sel) {
      const qty = parseFloat(seleccionados[it.id_detalle]) || 0;
      const pend = parseFloat(it.cantidad_pendiente) || 0;
      if (qty <= 0) { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" debe ser mayor a 0.` }); return; }
      if (qty > pend) { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" excede la pendiente (${fmtN(pend, 4)}).` }); return; }
    }

    const n = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;
    const payload = {
      id_almacen: n(form.id_almacen),
      id_sucursal: n(form.id_sucursal),
      id_tipo_movimiento: n(form.id_tipo_movimiento),
      fecha_operacion: form.fecha_operacion,
      observacion: form.observacion || null,
      id_centro_costo: n(form.id_centro_costo),
      id_solicitante: n(form.id_solicitante),
      id_guia_remision: grSeleccionada.id_guia,
      detalle: sel.map((it, i) => ({
        orden: i + 1,
        id_articulo: it.id_articulo,
        id_unidad: it.id_unidad || null,
        cantidad: parseFloat(seleccionados[it.id_detalle]) || 1,
        costo_unitario: 0,
        igv_pct: 0,
        id_detalle_gr: it.id_detalle,
      })),
    };
    setSaving(true);
    try {
      const res = await parteEntradaService.create(payload);
      setCreado(res.data);
      fetchGrs();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar la parte de entrada.') });
    } finally { setSaving(false); }
  };

  /* ── Columnas lista ───────────────────────────── */
  const tableColumns = [
    { key: 'serie_codigo', label: 'Serie', width: 70 },
    { key: 'numero', label: 'N°', width: 70,
      render: r => <span style={{ fontWeight: 700, color: '#60a5fa' }}>{r.numero}</span> },
    { key: 'fecha_guia', label: 'F. Guía', width: 92,
      render: r => (r.fecha_guia || '').slice(0, 10) || '—' },
    { key: 'almacen_origen_nombre', label: 'Origen',
      render: r => <span style={{ fontSize: 11 }}>{r.almacen_origen_nombre || '—'}<div style={{ fontSize: 10, color: '#64748b' }}>{r.sucursal_origen_nombre || ''}</div></span> },
    { key: 'almacen_destino_nombre', label: 'Destino',
      render: r => <span style={{ fontSize: 11 }}>{r.almacen_destino_nombre || '—'}<div style={{ fontSize: 10, color: '#64748b' }}>{r.sucursal_destino_nombre || ''}</div></span> },
    { key: 'rq_numero', label: 'RQ Origen', width: 100,
      render: r => r.rq_numero ? <span style={{ fontSize: 11, color: '#a78bfa' }}>{r.rq_serie || 'RQ'}-{r.rq_numero}</span> : '—' },
    { key: 'estado', label: 'Estado', width: 86,
      render: r => <span style={{ background: ESTADO_GR[r.estado]?.bg, color: ESTADO_GR[r.estado]?.color, border: `1px solid ${ESTADO_GR[r.estado]?.border}`, borderRadius: 8, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>{r.estado}</span> },
    { key: 'atencion', label: 'Pendiente', width: 90,
      render: r => {
        const tot = parseFloat(r.cantidad_total) || 0;
        const rec = parseFloat(r.cantidad_recibida) || 0;
        const pend = tot - rec;
        const color = pend <= 0 ? '#22c55e' : rec > 0 ? '#fb923c' : '#60a5fa';
        return <span style={{ color, fontWeight: 700, fontSize: 11 }}>{fmtN(pend, 2)}</span>;
      }},
    { key: '_accion', label: 'Acción', width: 84,
      render: r => {
        const tot = parseFloat(r.cantidad_total) || 0;
        const rec = parseFloat(r.cantidad_recibida) || 0;
        const atendido = tot > 0 && rec >= tot;
        return (
          <button onClick={() => !atendido && seleccionarGr(r)} disabled={atendido}
            style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
              border: '1px solid', cursor: atendido ? 'not-allowed' : 'pointer',
              background: atendido ? 'rgba(255,255,255,0.03)' : 'rgba(96,165,250,0.12)',
              color: atendido ? '#374151' : '#60a5fa',
              borderColor: atendido ? 'rgba(255,255,255,0.06)' : 'rgba(96,165,250,0.35)' }}>
            {atendido ? 'Recibida' : 'Recibir →'}
          </button>
        );
      }},
  ];

  const totales = useMemo(() => {
    let q = 0;
    grItems.forEach(it => { if (it.id_detalle in seleccionados) q += parseFloat(seleccionados[it.id_detalle]) || 0; });
    return q;
  }, [grItems, seleccionados]);

  const almacenesFiltrados = useMemo(
    () => form.id_sucursal
      ? almacenes.filter(a => String(a.id_sucursal) === String(form.id_sucursal))
      : almacenes,
    [almacenes, form.id_sucursal]
  );

  /* ──────────────────────────────────────────────── VIEW: LISTA ────── */
  if (view === 'list') {
    return (
      <Layout title="PE desde Guía de Remisión" subtitle="Recepciona mercadería trasladada desde otra sucursal">
        <div style={{ padding: 20 }}>
          <DataTable
            columns={tableColumns}
            data={grsLista}
            idField="id_guia"
            loading={loading}
            emptyMessage="No hay guías de remisión pendientes de recepción"
          />
        </div>
        {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      </Layout>
    );
  }

  /* ──────────────────────────────────────────────── VIEW: CREADO ───── */
  if (view === 'creado' || creado) {
    return (
      <Layout title="PE creado" subtitle="Recepción de GR registrada">
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ color: '#22c55e' }}>✓ Parte de Entrada creado</h2>
          <p style={{ color: '#94a3b8' }}>PE N° {creado?.numero} — {creado?.fecha_operacion?.slice(0, 10)}</p>
          <button onClick={() => { setCreado(null); setView('list'); setGrSeleccionada(null); }}
            style={{ marginTop: 20, padding: '10px 24px', fontSize: 13, fontWeight: 600, background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            Volver al listado
          </button>
        </div>
      </Layout>
    );
  }

  /* ──────────────────────────────────────────────── VIEW: FORM ─────── */
  return (
    <Layout title={`PE desde ${grRef(grSeleccionada?.serie_codigo, grSeleccionada?.numero)}`} subtitle={`Origen: ${grSeleccionada?.almacen_origen_nombre || '—'}`}>
      <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Form */}
        <div style={{ background: 'rgba(255,255,255,0.025)', borderRadius: 10, padding: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 style={{ fontSize: 14, color: '#e2e8f0', marginBottom: 12 }}>Datos del Parte de Entrada</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <AutoComplete label="Sucursal" value={form.id_sucursal} options={sucursales} idField="id_sucursal" displayField="nombre"
              onChange={v => setForm(p => ({ ...p, id_sucursal: v, id_almacen: '' }))} placeholder="Sucursal..." />
            <AutoComplete label="Almacén *" value={form.id_almacen} options={almacenesFiltrados} idField="id_almacen" displayField="nombre"
              error={!form.id_almacen}
              onChange={v => setForm(p => ({ ...p, id_almacen: v }))} placeholder="Almacén..." />
            <AutoComplete label="Tipo Movimiento *" value={form.id_tipo_movimiento} options={tiposMov} idField="id_tipo_movimiento" displayField="descripcion"
              error={!form.id_tipo_movimiento}
              onChange={v => setForm(p => ({ ...p, id_tipo_movimiento: v }))} placeholder="Tipo..." />
            <div className="form-row">
              <label>Fecha Operación *</label>
              <input type="date" value={form.fecha_operacion} onChange={e => setForm(p => ({ ...p, fecha_operacion: e.target.value }))} />
            </div>
            <AutoComplete label="Solicitante" value={form.id_solicitante} options={trabajadores} idField="id_trabajador" displayField="nombres"
              onChange={v => setForm(p => ({ ...p, id_solicitante: v }))} placeholder="Solicitante..." />
            <AutoComplete label="Centro de Costo" value={form.id_centro_costo} options={centrosCosto} idField="id_centro_costo" displayField="descripcion"
              onChange={v => setForm(p => ({ ...p, id_centro_costo: v }))} placeholder="Centro costo..." />
            <div className="form-row" style={{ gridColumn: '1 / -1' }}>
              <label>Observación</label>
              <input value={form.observacion} onChange={e => setForm(p => ({ ...p, observacion: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop: 12, padding: 10, background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)', borderRadius: 8, fontSize: 11, color: '#94a3b8' }}>
            <div>Correlativo siguiente: <strong style={{ color: '#60a5fa' }}>PE-{nextNumero ?? '—'}</strong></div>
            <div style={{ marginTop: 4 }}>Ítems seleccionados: <strong style={{ color: '#e2e8f0' }}>{Object.keys(seleccionados).length}</strong> · Cantidad total: <strong style={{ color: '#e2e8f0' }}>{fmtN(totales, 2)}</strong></div>
          </div>
        </div>

        {/* Items de la GR */}
        <div style={{ background: 'rgba(255,255,255,0.025)', borderRadius: 10, padding: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 style={{ fontSize: 14, color: '#e2e8f0', marginBottom: 12 }}>Ítems de la Guía</h3>
          {loadingItems ? <div style={{ color: '#64748b', fontSize: 12 }}>Cargando...</div> : (
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', textAlign: 'left' }}>
                  <th style={{ padding: 6, width: 30 }}>
                    <input type="checkbox" onChange={toggleAll}
                      checked={grItems.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0).length > 0 && grItems.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0).every(it => it.id_detalle in seleccionados)} />
                  </th>
                  <th style={{ padding: 6 }}>Artículo</th>
                  <th style={{ padding: 6, textAlign: 'right', width: 70 }}>Pend.</th>
                  <th style={{ padding: 6, textAlign: 'right', width: 90 }}>Cant. Recibe</th>
                </tr>
              </thead>
              <tbody>
                {grItems.map(it => {
                  const sel = it.id_detalle in seleccionados;
                  const pend = parseFloat(it.cantidad_pendiente) || 0;
                  return (
                    <tr key={it.id_detalle} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: pend <= 0 ? 0.5 : 1 }}>
                      <td style={{ padding: 6 }}>
                        <input type="checkbox" checked={sel} onChange={() => toggleItem(it)} disabled={pend <= 0} />
                      </td>
                      <td style={{ padding: 6 }}>
                        <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{it.articulo_descripcion}</div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>{it.articulo_codigo} · {it.unidad_descripcion || '—'}</div>
                      </td>
                      <td style={{ padding: 6, textAlign: 'right', fontFamily: 'monospace', color: pend <= 0 ? '#22c55e' : '#fb923c' }}>{fmtN(pend, 2)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>
                        {sel ? (
                          <input type="number" step="0.0001" min="0" max={pend} value={seleccionados[it.id_detalle] || ''}
                            onChange={e => setCantidad(it.id_detalle, e.target.value)}
                            style={{ width: 80, padding: '3px 6px', textAlign: 'right', fontSize: 11, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, color: '#e2e8f0' }} />
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {grItems.length === 0 && (
                  <tr><td colSpan="4" style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>Sin ítems</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 20px 20px' }}>
        <button onClick={() => { setView('list'); setGrSeleccionada(null); }}
          style={{ padding: '10px 20px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#94a3b8', borderRadius: 8, cursor: 'pointer' }}>
          Cancelar
        </button>
        <button onClick={handleSubmit} disabled={saving}
          style={{ padding: '10px 24px', fontSize: 12, fontWeight: 600, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: 'white', borderRadius: 8, cursor: 'pointer' }}>
          {saving ? 'Guardando...' : 'Guardar PE'}
        </button>
      </div>

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
    </Layout>
  );
};

export default PeDesdeGr;
